// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@conklin/App/Components/Notify";
import { updateXCheckoutState } from "@conklin/App/Handlers/Checkout/xCheckout";
import checkoutSharedStyles from "@conklin/App/SharedStyles/CheckoutStyles";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import Select from "@conklin/MobiusOverrides/Select";
import { PickUpPreferences, ShippingPreferences } from "@conklin/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import flatMap from "lodash/flatMap";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const {
        accountSettings: { enableWarehousePickup, allowEmptyShipping },
        cartSettings: { canRequestDeliveryDate, enableRequestPickUpDate, maximumDeliveryPeriod },
    } = getSettingsCollection(state);
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart, isLoading: isCartLoading } = cartState;
    const session = getSession(state);
    const fulfillmentMethod = cart?.fulfillmentMethod || session.fulfillmentMethod;
    const pickUpWarehouse = cart?.defaultWarehouse || session.pickUpWarehouse;
    return {
        cart,
        isCartLoading,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showRequestPickUpDate:
            enableWarehousePickup && enableRequestPickUpDate && fulfillmentMethod === FulfillmentMethod.PickUp,
        showRequestDeliveryDate: canRequestDeliveryDate,
        maximumDeliveryPeriod,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        pickUpWarehouse,
        allowEmptyShipping,
        // XNG-Changes
        shipWhenComplete: state.xCheckout.shipWhenComplete,
        pickupWhenComplete: state.xCheckout.pickupWhenComplete,
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    // XNG-Changes
    updateXCheckoutState,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutDeliveryMethodStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    fulfillmentMethodGridItem?: GridItemProps;
    fulfillmentMethodText?: TypographyPresentationProps;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    servicesGridItem?: GridItemProps;
    servicesRadioGroup?: RadioGroupPresentationProps;
    serviceRadio?: RadioPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    // XNG-Changes
    recommendedShippingMethodMessage?: TypographyPresentationProps;
}

export const onePageCheckoutDeliveryMethodStyles: OnePageCheckoutDeliveryMethodStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        transform: "capitalize",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    fulfillmentMethodGridItem: { width: 12 },
    fulfillmentMethodText: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    servicesGridItem: { width: 12 },
    deliveryDateGridItem: { width: 12 },
    pickUpDateGridItem: { width: 12 },
};

const styles = onePageCheckoutDeliveryMethodStyles;

const OnePageCheckoutDeliveryMethod = ({
    cart,
    isCartLoading,
    cartUrl,
    pickUpWarehouse,
    showShippingMethod,
    showRequestPickUpDate,
    showRequestDeliveryDate,
    maximumDeliveryPeriod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setShippingMethod,
    allowEmptyShipping,
    // XNG-Changes
    shipWhenComplete,
    pickupWhenComplete,
    updateXCheckoutState,
}: Props) => {
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [isUpdatingCart, setIsUpdatingCart] = useState(false);
    useEffect(() => {
        if (cart) {
            setSelectedShipVia(cart.shipVia);
            setIsUpdatingCart(false);
        }
    }, [cart?.carrier, cart?.shipVia]);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        // XNG-Changes
        return null;
        // return (
        //     <StyledWrapper {...styles.centeringWrapper}>
        //         <LoadingSpinner data-test-selector="onePageCheckout_deliveryMethodLoading" />
        //     </StyledWrapper>
        // );
    }

    // XNG-Changes: add HTMLSelectElement
    const serviceChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (!cart.carriers) {
            return;
        }

        const shipVias = flatMap(cart.carriers, o => o.shipVias).filter(o => !!o) as ShipViaDto[];
        const shipVia = shipVias.find(o => o.id === event.currentTarget.value);
        if (!shipVia) {
            return;
        }

        const carrier = cart.carriers.find(o => o.shipVias?.some(p => p.id === shipVia.id));
        if (!carrier) {
            return;
        }

        setIsUpdatingCart(true);
        setSelectedShipVia(shipVia);
        setShippingMethod({ carrier, shipVia });
    };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };

    const getMinDate = () => {
        const now = new Date();
        const currentHour = now.getHours();
        // 3 PM
        if (currentHour < 15) {
            return now;
        }
        now.setDate(now.getDate() + 1);
        return now;
    };

    // XNG-Changes: structure
    return (
        <>
            <Typography {...checkoutSharedStyles.containerTitle}>
                {cart.fulfillmentMethod === FulfillmentMethod.PickUp ? translate("Pick Up") : translate("Shipping")}
            </Typography>
            <StyledWrapper {...checkoutSharedStyles.container}>
                {showShippingMethod && (
                    <StyledWrapper {...checkoutSharedStyles.section}>
                        {/* XNG-Changes
                    <Typography
                        as="p"
                        {...styles.fulfillmentMethodText}
                        data-test-selector="onePageCheckout_fulfillmentMethod"
                    >
                        {showShippingMethod
                            ? translate("Ship to home")
                            : `${translate("Store pickup at")} ${pickUpWarehouse?.description || pickUpWarehouse?.name}`}
                    </Typography> 
                */}
                        {cart.carriers?.length === 0 && !allowEmptyShipping && (
                            <Notify type="warning" showClose={false} {...styles.noCarriersFoundText}>
                                {siteMessage("ReviewAndPay_NoCarriersFound")}
                            </Notify>
                        )}
                        {cart.carriers.length > 0 && (
                            <StyledWrapper {...styles.servicesGridItem}>
                                {/* <RadioGroup
                                    {...styles.servicesRadioGroup}
                                    label={translate("Choose delivery method:")}
                                    value={selectedShipVia?.id}
                                    disabled={isCartLoading || isUpdatingCart}
                                    onChangeHandler={serviceChangeHandler}
                                    data-test-selector="onePageCheckout_chooseDeliveryMethod"
                                >
                                    {flatMap(cart.carriers, carrier =>
                                        carrier.shipVias?.map(shipVia => ({ ...shipVia, carrier })),
                                    ).map(shipVia => (
                                        <Radio
                                            {...styles.serviceRadio}
                                            key={shipVia?.id}
                                            value={shipVia?.id}
                                            data-test-selector={`${shipVia?.carrier.description} - ${shipVia?.description}`}
                                        >
                                            {shipVia?.carrier.description} - {shipVia?.description}
                                        </Radio>
                                    ))}
                                </RadioGroup> */}
                                <Select
                                    label={translate("Shipping Method")}
                                    value={selectedShipVia?.id}
                                    disabled={isCartLoading || isUpdatingCart}
                                    onChange={serviceChangeHandler}
                                    data-test-selector="onePageCheckout_chooseDeliveryMethod"
                                >
                                    {flatMap(cart.carriers, carrier =>
                                        carrier.shipVias?.map(shipVia => ({ ...shipVia, carrier })),
                                    ).map(shipVia => (
                                        <option
                                            {...styles.serviceRadio}
                                            key={shipVia?.id}
                                            value={shipVia?.id}
                                            data-test-selector={`${shipVia?.carrier.description} - ${shipVia?.description}`}
                                        >
                                            {shipVia?.isDefault
                                                ? `${translate("Default:")} ${shipVia?.description}`
                                                : shipVia?.description}
                                        </option>
                                    ))}
                                </Select>
                                {/* XNG-Changes */}
                                <Typography {...styles.recommendedShippingMethodMessage}>
                                    {siteMessage("Custom_ConklinRecommendedShippingMethod_Message")}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
                <StyledWrapper {...checkoutSharedStyles.section}>
                    {/* XNG-Changes */}
                    {cart.fulfillmentMethod === FulfillmentMethod.PickUp ? (
                        <RadioGroup
                            name="pickupWhenComplete"
                            value={pickupWhenComplete}
                            onChangeHandler={e => updateXCheckoutState({ pickupWhenComplete: e.currentTarget.value })}
                            horizontal={true}
                        >
                            <Radio value="false">{translate(PickUpPreferences.PickUpWhenReady)}</Radio>
                            <Radio value="true">{translate(PickUpPreferences.PickUpWhenComplete)}</Radio>
                        </RadioGroup>
                    ) : (
                        <RadioGroup
                            name="shipWhenComplete"
                            label={translate("Shipping Preference")}
                            value={shipWhenComplete}
                            onChangeHandler={e => updateXCheckoutState({ shipWhenComplete: e.currentTarget.value })}
                            horizontal={true}
                        >
                            <Radio value="false">{translate(ShippingPreferences.ShipWhenAvailable)}</Radio>
                            <Radio value="true">{translate(ShippingPreferences.ShipWhenComplete)}</Radio>
                        </RadioGroup>
                    )}

                    {cart.fulfillmentMethod === FulfillmentMethod.Ship && showRequestDeliveryDate && (
                        <StyledWrapper
                            {...styles.deliveryDateGridItem}
                            data-test-selector="onePageCheckout_requestedDeliveryDate"
                        >
                            <DatePicker
                                {...styles.deliveryDatePicker}
                                // XNG-Changes
                                label={`${translate("Request Delivery Date")} (${translate("Optional")})`}
                                hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                selectedDay={cart.requestedDeliveryDateDisplay!}
                                dateTimePickerProps={{
                                    minDate: new Date(),
                                    maxDate: addDays(new Date(), maximumDeliveryPeriod),
                                    monthAriaLabel: translate("Request delivery date month"),
                                    dayAriaLabel: translate("Request delivery date day"),
                                    yearAriaLabel: translate("Request delivery date year"),
                                    ...styles.deliveryDatePicker?.dateTimePickerProps,
                                }}
                                onDayChange={handleRequestDeliveryDateChanged}
                            />
                        </StyledWrapper>
                    )}
                    {cart.fulfillmentMethod === FulfillmentMethod.PickUp && showRequestPickUpDate && (
                        <StyledWrapper
                            {...styles.pickUpDateGridItem}
                            data-test-selector="onePageCheckout_requestPickUpDate"
                        >
                            <DatePicker
                                {...styles.pickUpDatePicker}
                                label={`${translate("Request Pick Up Date")} (${translate("optional")})`}
                                hint={
                                    <>
                                        <Typography>
                                            {siteMessage("Checkout_RequestedPickupDateInformation")}
                                        </Typography>
                                        {new Date().getHours() < 15 && (
                                            <Typography>
                                                {siteMessage("Custom_DatePlacedAfter3Pm_Notify_Msg")}
                                            </Typography>
                                        )}
                                    </>
                                }
                                selectedDay={cart.requestedPickupDateDisplay!}
                                dateTimePickerProps={{
                                    minDate: getMinDate(),
                                    maxDate: addDays(new Date(), maximumDeliveryPeriod),
                                    ...styles.pickUpDatePicker?.dateTimePickerProps,
                                }}
                                onDayChange={handleRequestPickUpDateChanged}
                                monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                    "Enter month, format M M",
                                )}`}
                                dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                    "Enter day, format D D",
                                )}`}
                                yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                    "Enter year, format Y Y Y Y",
                                )}`}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutDeliveryMethod),
    definition: {
        displayName: "Delivery Method",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
